import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { gql, useMutation } from '@apollo/client';
import { Country, State, City } from 'country-state-city';
import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import 'react-phone-number-input/style.css';
import '../components/PhoneInput.css';

const REQUEST_STAFF_CREATION_OTP = gql`
  mutation RequestStaffCreationOTP($phone: String!) {
    requestStaffCreationOTP(phone: $phone) {
      success
      message
      expiresIn
    }
  }
`;

const VERIFY_STAFF_CREATION_OTP = gql`
  mutation VerifyStaffCreationOTP(
    $phone: String!
    $otp: String!
    $firstName: String!
    $lastName: String!
    $email: String!
    $address_line_1: String
    $address_line_2: String
    $city: String
    $state: String
    $zip_code: String
  ) {
    verifyStaffCreationOTP(
      phone: $phone
      otp: $otp
      firstName: $firstName
      lastName: $lastName
      email: $email
      address_line_1: $address_line_1
      address_line_2: $address_line_2
      city: $city
      state: $state
      zip_code: $zip_code
    ) {
      id
      username
      email
      mobile
      firstName
      lastName
      role
      is_active
      created_at
    }
  }
`;

const StaffCreation = ({ onClose, onStaffCreated }) => {
  const { user } = useSelector((state) => state.auth);
  const [requestOTP] = useMutation(REQUEST_STAFF_CREATION_OTP);
  const [verifyOTP] = useMutation(VERIFY_STAFF_CREATION_OTP);

  // Step management
  const [currentStep, setCurrentStep] = useState(1); // 1: Phone, 2: OTP, 3: Details
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Phone verification states
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResendOtp, setCanResendOtp] = useState(false);

  // Staff details states
  const [staffData, setStaffData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    zip_code: '',
  });

  // Location data for dropdowns
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [detectedCountry, setDetectedCountry] = useState('IN');

  // Auto-detect country from mobile number
  const getCountryFromMobile = (mobile) => {
    if (!mobile) return 'IN';

    const cleanMobile = mobile.replace(/\D/g, '');

    if (cleanMobile.startsWith('91')) return 'IN'; // India
    if (cleanMobile.startsWith('1')) return 'US'; // USA/Canada
    if (cleanMobile.startsWith('44')) return 'GB'; // UK
    if (cleanMobile.startsWith('61')) return 'AU'; // Australia
    if (cleanMobile.startsWith('33')) return 'FR'; // France
    if (cleanMobile.startsWith('49')) return 'DE'; // Germany
    if (cleanMobile.startsWith('81')) return 'JP'; // Japan
    if (cleanMobile.startsWith('86')) return 'CN'; // China

    return 'IN';
  };

  // Get country name for display
  const getCountryName = (countryCode) => {
    const country = countries.find(c => c.isoCode === countryCode);
    return country ? country.name : 'India';
  };

  // Load countries on component mount
  useEffect(() => {
    const countryList = Country.getAllCountries();
    setCountries(countryList);
  }, []);

  // Update detected country when phone changes
  useEffect(() => {
    if (phone) {
      const country = getCountryFromMobile(phone);
      setDetectedCountry(country);
    }
  }, [phone]);

  // Load states when country changes
  useEffect(() => {
    if (detectedCountry) {
      const stateList = State.getStatesOfCountry(detectedCountry);
      setStates(stateList);
      setCities([]);
      setStaffData(prev => ({ ...prev, state: '', city: '' }));
    }
  }, [detectedCountry]);

  // Load cities when state changes
  useEffect(() => {
    if (detectedCountry && staffData.state) {
      const cityList = City.getCitiesOfState(detectedCountry, staffData.state);
      setCities(cityList);
      setStaffData(prev => ({ ...prev, city: '' }));
    }
  }, [detectedCountry, staffData.state]);

  // OTP Timer countdown
  useEffect(() => {
    if (otpTimer > 0) {
      const timer = setTimeout(() => {
        setOtpTimer(otpTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (otpTimer === 0 && currentStep === 2) {
      setCanResendOtp(true);
    }
  }, [otpTimer, currentStep]);

  // Phone validation
  const validatePhone = (phoneValue) => {
    if (!phoneValue) {
      setPhoneError('Phone number is required');
      return false;
    }

    if (!isValidPhoneNumber(phoneValue)) {
      setPhoneError('Please enter a valid phone number');
      return false;
    }

    setPhoneError('');
    return true;
  };

  // Handle phone submission and OTP request
  const handlePhoneSubmit = async () => {
    if (!validatePhone(phone)) return;

    setLoading(true);
    setError('');

    try {
      console.log('Requesting OTP for phone:', phone);

      const result = await requestOTP({
        variables: { phone }
      });

      console.log('Full OTP request result:', result);

      const data = result?.data;
      const otpResponse = data?.requestStaffCreationOTP;

      if (otpResponse?.success) {
        console.log('OTP sent successfully');
        setCurrentStep(2);
        setOtpTimer(300); // 5 minutes
        setCanResendOtp(false);
        setOtp(['', '', '', '', '', '']);
      } else {
        const errorMessage = otpResponse?.message || 'Failed to send OTP. Please try again.';
        console.error('OTP request failed:', errorMessage);
        setError(errorMessage);
      }
    } catch (err) {
      console.error('OTP request error:', err);
      console.error('Error details:', {
        message: err.message,
        graphQLErrors: err.graphQLErrors,
        networkError: err.networkError
      });

      let errorMessage = 'Failed to send OTP. Please try again.';

      if (err.graphQLErrors && err.graphQLErrors.length > 0) {
        errorMessage = err.graphQLErrors[0].message;
      } else if (err.networkError) {
        errorMessage = 'Network error. Please check your connection.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    // Auto-verify when all digits are entered
    if (newOtp.every(digit => digit !== '') && newOtp.join('').length === 6) {
      handleOtpVerify(newOtp.join(''));
    }
  };

  // Handle OTP verification
  const handleOtpVerify = async (otpValue = null) => {
    const otpString = otpValue || otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Just verify OTP, don't create user yet
      setCurrentStep(3);
    } catch (err) {
      console.error('OTP verification error:', err);
      setError(err.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle staff creation
  const handleStaffCreation = async () => {
    // Validate required fields
    if (!staffData.firstName || !staffData.lastName || !staffData.email) {
      setError('First name, last name, and email are required');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(staffData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('Creating staff with data:', {
        phone,
        otp: otp.join(''),
        ...staffData
      });

      const result = await verifyOTP({
        variables: {
          phone,
          otp: otp.join(''),
          ...staffData
        }
      });

      console.log('Staff creation result:', result);

      const data = result?.data;
      const staffResponse = data?.verifyStaffCreationOTP;

      if (staffResponse) {
        console.log('Staff member created successfully:', staffResponse);
        onStaffCreated && onStaffCreated(staffResponse);
        onClose();
      } else {
        setError('Failed to create staff member. Please try again.');
      }
    } catch (err) {
      console.error('Staff creation error:', err);
      console.error('Error details:', {
        message: err.message,
        graphQLErrors: err.graphQLErrors,
        networkError: err.networkError
      });

      let errorMessage = 'Failed to create staff member. Please try again.';

      if (err.graphQLErrors && err.graphQLErrors.length > 0) {
        errorMessage = err.graphQLErrors[0].message;
      } else if (err.networkError) {
        errorMessage = 'Network error. Please check your connection.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle resend OTP
  const handleResendOtp = async () => {
    setLoading(true);
    setError('');

    try {
      const { data } = await requestOTP({
        variables: { phone }
      });

      console.log('Resend OTP response:', data);

      if (data?.requestStaffCreationOTP?.success) {
        setOtpTimer(300);
        setCanResendOtp(false);
        setOtp(['', '', '', '', '', '']);
        setError('');
      } else {
        setError(data?.requestStaffCreationOTP?.message || 'Failed to resend OTP. Please try again.');
      }
    } catch (err) {
      console.error('Resend OTP error:', err);
      setError(err.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-bulky-navy-800 to-bulky-navy-950 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-medium">Create Staff Member</h2>
              <p className="text-bulky-navy-300 text-sm mt-1">
                Step {currentStep} of 3: {
                  currentStep === 1 ? 'Phone Verification' :
                    currentStep === 2 ? 'OTP Verification' :
                      'Staff Details'
                }
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-bulky-navy-300 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-normal ${step <= currentStep ? 'bg-bulky-blue-500 text-white' : 'bg-bulky-navy-600 text-bulky-navy-400'
                    }`}>
                    {step}
                  </div>
                  {step < 3 && (
                    <div className={`w-12 h-1 mx-2 ${step < currentStep ? 'bg-bulky-blue-500' : 'bg-bulky-navy-600'
                      }`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Step 1: Phone Number */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-medium">Enter Staff Member's Phone Number</h2>
                <p className="text-gray-600 text-sm mb-4">
                  We'll send an OTP to verify this phone number before creating the staff account.
                </p>
              </div>

              <div>
                <label className="block text-sm font-normal text-gray-700 mb-2">
                  Phone Number
                </label>
                <PhoneInput
                  international
                  defaultCountry="IN"
                  value={phone}
                  onChange={setPhone}
                  className="w-full"
                  placeholder="Enter phone number"
                />
                {phoneError && (
                  <p className="mt-1 text-sm text-red-600">{phoneError}</p>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handlePhoneSubmit}
                  disabled={loading || !phone}
                  className="px-6 py-2 bg-bulky-blue-500 text-white rounded-lg hover:bg-bulky-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Sending OTP...</span>
                    </>
                  ) : (
                    <>
                      <span>Send OTP</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 2: OTP Verification */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-medium">Verify Phone Number</h2>
                <p className="text-gray-600 text-sm mb-4">
                  Enter the 6-digit OTP sent to <strong>{phone}</strong>
                </p>
              </div>

              <div>
                <label className="block text-sm font-normal text-gray-700 mb-2">
                  Enter OTP
                </label>
                <div className="flex gap-2 justify-center">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      id={`otp-${index}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      className="w-12 h-12 text-center text-xl font-normal border-2 border-gray-300 rounded-lg focus:border-bulky-blue-500 focus:outline-none"
                    />
                  ))}
                </div>
              </div>

              {otpTimer > 0 && (
                <p className="text-center text-sm text-gray-600">
                  OTP expires in {formatTime(otpTimer)}
                </p>
              )}

              {canResendOtp && (
                <div className="text-center">
                  <button
                    onClick={handleResendOtp}
                    disabled={loading}
                    className="text-bulky-blue-500 hover:text-bulky-blue-600 text-sm font-normal"
                  >
                    Resend OTP
                  </button>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  disabled={loading}
                >
                  Back
                </button>
                <button
                  onClick={() => handleOtpVerify()}
                  disabled={loading || otp.some(digit => !digit)}
                  className="px-6 py-2 bg-bulky-blue-500 text-white rounded-lg hover:bg-bulky-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <span>Verify OTP</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Staff Details */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-medium">Staff Member Details</h2>
                <p className="text-gray-600 text-sm mb-4">
                  Enter the staff member's personal information. Business details will be inherited from your account.
                </p>
              </div>

              {/* Business Information (Read-only) */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h2 className="font-medium">Business Information (Inherited)</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Owner:</span>
                    <span className="ml-2 font-normal">{user?.ownerName || 'Not set'}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Company:</span>
                    <span className="ml-2 font-normal">{user?.companyName || 'Not set'}</span>
                  </div>
                  <div className="md:col-span-2">
                    <span className="text-gray-500">Industry:</span>
                    <span className="ml-2 font-normal">{user?.businessIndustry || 'Not set'}</span>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-normal text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={staffData.firstName}
                    onChange={(e) => setStaffData(prev => ({ ...prev, firstName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-bulky-blue-500 focus:outline-none"
                    placeholder="Enter first name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-normal text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={staffData.lastName}
                    onChange={(e) => setStaffData(prev => ({ ...prev, lastName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-bulky-blue-500 focus:outline-none"
                    placeholder="Enter last name"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-normal text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={staffData.email}
                    onChange={(e) => setStaffData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-bulky-blue-500 focus:outline-none"
                    placeholder="Enter email address"
                  />
                </div>
              </div>

              {/* Address Information */}
              <div>
                <h2 className="font-medium">Address Information (Optional)</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-normal text-gray-700 mb-1">
                      Address Line 1
                    </label>
                    <input
                      type="text"
                      value={staffData.address_line_1}
                      onChange={(e) => setStaffData(prev => ({ ...prev, address_line_1: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-bulky-blue-500 focus:outline-none"
                      placeholder="Enter address line 1"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-normal text-gray-700 mb-1">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      value={staffData.address_line_2}
                      onChange={(e) => setStaffData(prev => ({ ...prev, address_line_2: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-bulky-blue-500 focus:outline-none"
                      placeholder="Enter address line 2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-normal text-gray-700 mb-1">
                      State
                    </label>
                    <select
                      value={staffData.state}
                      onChange={(e) => setStaffData(prev => ({ ...prev, state: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-bulky-blue-500 focus:outline-none"
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state.isoCode} value={state.isoCode}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-normal text-gray-700 mb-1">
                      City
                    </label>
                    <select
                      value={staffData.city}
                      onChange={(e) => setStaffData(prev => ({ ...prev, city: e.target.value }))}
                      disabled={!staffData.state}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-bulky-blue-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="">Select City</option>
                      {cities.map((city) => (
                        <option key={city.name} value={city.name}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-normal text-gray-700 mb-1">
                      Country
                    </label>
                    <input
                      type="text"
                      value={getCountryName(detectedCountry)}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">Auto-detected from phone number</p>
                  </div>

                  <div>
                    <label className="block text-sm font-normal text-gray-700 mb-1">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      value={staffData.zip_code}
                      onChange={(e) => setStaffData(prev => ({ ...prev, zip_code: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-bulky-blue-500 focus:outline-none"
                      placeholder="Enter ZIP code"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  disabled={loading}
                >
                  Back
                </button>
                <button
                  onClick={handleStaffCreation}
                  disabled={loading || !staffData.firstName || !staffData.lastName || !staffData.email}
                  className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      <span>Creating Staff...</span>
                    </>
                  ) : (
                    <>
                      <span>Create Staff Member</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffCreation;